/**
 * DEFCAT-R2-04 — read-only detail for a selected L0 reference node.
 *
 * L0 = a global component group (`companies_db.global_component_groups`),
 * referenced by location entries via `componentGroupId`. It is NOT a catalogue
 * entry, so it offers no edit form, no retire, no parent picker — just context:
 * the group name and a note that it is managed in master data. (R2-04 AC5.)
 */
import { type ReactElement } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { TreeNode } from "./treeBuild.js";
import { locText } from "./locText.js";

/**
 * Section-heading style matching the analysis filter widget's `LevelHeader`
 * (the platform's canonical compact-widget scale).
 */
const sectionHeadingSx = {
  fontSize: typography.size["label-md"],
  fontWeight: typography.weight.semibold,
  letterSpacing: typography.tracking["label-md"],
  color: "text.secondary",
  textTransform: "uppercase" as const,
};

export interface L0DetailPanelProps {
  node: TreeNode;
}

export function L0DetailPanel({ node }: L0DetailPanelProps): ReactElement {
  const cg = node.l0?.componentGroupId ?? null;
  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1.5}>
        <Typography component="span" sx={sectionHeadingSx}>
          Component group (L0)
        </Typography>
        {/* Group name — emphasized but at the compact widget body-md scale
            (15), not the oversized h6 default. */}
        <Typography
          sx={{
            fontSize: typography.size["body-md"],
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["body-md"],
          }}
        >
          {locText(node.entry.name)}
        </Typography>
        <Alert
          severity="info"
          sx={{
            py: 0,
            "& .MuiAlert-message": { fontSize: typography.size["caption-lg"] },
          }}
        >
          Global component group — managed in master data. This is a reference
          node: location entries hang under it but it is not editable here.
        </Alert>
        <Typography
          color="text.secondary"
          sx={{ fontSize: typography.size["caption-lg"] }}
        >
          Component group id:{" "}
          {cg ?? "(none — locations without a component group)"}
        </Typography>
      </Stack>
    </Box>
  );
}
